import { StyledBreadcrumb } from "@aureskonnect/react-ui";
import PageTitle from "@components/Common/PageTitle";
import MessageInfo from "@components/Common/MessageInfo";
import {
    customColumnProps,
    DynamicTable,
} from "@maherunlocker/custom-react-table";
import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from "react";
import { useTranslation } from "react-i18next/";
import { useSnapshot } from "valtio";
import { store as useStore } from "@components/VerticalLayout/store";
import {
    setIsModalDissociateOpened,
    setModify,
    setNumberActive,
    setNumberArchived,
    setSelectedApplicationSco,
    store,
} from "./store";
import {
    setIsEdited,
    store as appStore,
    setIsActionsClicked,
    setActions,
    setValidationAction,
    setIsModesModalPublishOpened,
} from "../store";
import { CustomIcons } from "./CustomIcons";
import { CustomSideFilter } from "./CustomSideFilter";
import { AddProject } from "./AddProject";
import CustomColumnDisplay from "@components/Common/CustomColumnDisplay";
import { DropDownWrapper } from "./DropDownWrapper";
import { useLocation } from "react-router-dom";
import { getPathAuthorization } from "@components/VerticalLayout";
import { useCallbackPrompt } from "@hooks/useCallbackPrompt";
import { useSelector } from "react-redux";
import { useAuthHeader } from "../../../../api/headersKeycloak";
import { Dropdown } from "react-bootstrap";
import moment from "moment";
import ModalsContainer from "./ModalsContainer";
import { API_PROJECT_URL, ProjectStatus } from "./constants";

// Hook personnalisé pour regrouper les états modaux
const useModalStates = () => {
    const [modals, setModals] = useState({
        history: false,
        deviceDissociate: false,
        confirmArchived: false,
        confirmActived: false,
        confirmCopied: false,
        inInitialization: false,
    });

    const setModal = useCallback((key: keyof typeof modals, value: boolean) => {
        setModals((prev) => ({ ...prev, [key]: value }));
    }, []);

    return [modals, setModal] as const;
};

// Composant StatusBadge extrait
const StatusBadge = ({
    row,
    isArchived,
    t,
}: {
    row: any;
    isArchived: boolean;
    t: any;
}) => {
    const {
        key_project,
        hour,
        definedTime,
        hourlyInterval,
        interval,
        startDate,
        endDate,
        time,
        days,
    } = row;
    const Statut = row.Statut ?? row.Status;

    if (!key_project) return null;

    const formatTime = () => {
        if (definedTime)
            return `${moment(startDate).format("DD/MM/YYYY")}-${moment(
                time
            ).format("h:mm a")}`;
        if (hourlyInterval && interval?.length === 1)
            return `De ${moment(startDate).format("DD/MM/YYYY")} à ${moment(
                endDate
            ).format("DD/MM/YYYY")}`;
        if (hourlyInterval && interval?.length > 1)
            return moment(startDate).format("DD/MM/YYYY");
        return "";
    };

    const badgeStyle = {
        paddingTop: "8px",
        paddingBottom: "9px",
        fontSize: "10px",
        textAlign: "center" as const,
        fontWeight: "bold" as const,
    };

    if (Statut === "en attente" || Statut === ProjectStatus.PENDING) {
        return (
            <h4 style={{ paddingTop: "8px" }}>
                <span
                    className={
                        isArchived
                            ? "badge-secondary_clz"
                            : "badge badge-pill badge-danger"
                    }
                    style={badgeStyle}
                >
                    {isArchived ? t("Archived") : t("pending")}
                    <br />
                    {hour}
                </span>
            </h4>
        );
    }

    if (Statut === ProjectStatus.PUBLISHED) {
        return (
            <h4 style={{ paddingTop: "8px" }}>
                <span className="badge-success__clz" style={badgeStyle}>
                    {isArchived ? t("Archived") : t("Published")}
                    <br />
                    {hour}
                </span>
            </h4>
        );
    }

    if (Statut === ProjectStatus.PLANNED) {
        const toggleStyle = {
            paddingTop: "3px",
            paddingBottom: "3px",
            fontSize: "10px",
            textAlign: "center" as const,
            borderRadius: "25px",
            color: isArchived ? "#808080" : "#8811f9",
            backgroundColor: "#ebdffe",
            borderColor: "#ebdffe",
            fontWeight: "bold" as const,
        };

        return (
            <Dropdown>
                <Dropdown.Toggle
                    id="dropdown-button-dark-example1"
                    style={toggleStyle}
                    className="style-button-planing__clz"
                >
                    {isArchived ? t("Archived") : t("Planned")}
                    <br />
                    {formatTime()}
                </Dropdown.Toggle>
                {hourlyInterval && (
                    <Dropdown.Menu>
                        {(interval?.length > 1 ? days : [days?.[0]]).map(
                            (day: any, i: number) => (
                                <Dropdown.Item key={i}>
                                    {moment(day?.Start).format("h:mm a")}-
                                    {moment(day?.End).format("h:mm a")}
                                </Dropdown.Item>
                            )
                        )}
                    </Dropdown.Menu>
                )}
            </Dropdown>
        );
    }

    return null;
};

export default function PosProjectSetup() {
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const location = useLocation();
    const {
        isEdited,
        actions,
        validationAction,
        isActionsClicked,
        isModesModalPublishOpened,
    } = useSnapshot(appStore);
    const { oneShop, userID, franchiseID, shopID } = useSnapshot(useStore);
    const { isModalAssociateStore, isModalDissociateOpened } = useSnapshot(
        store
    );

    const selectedAccount = useSelector(
        (state: any) => state.global.selectedAccount
    );
    const authorizationList = useSelector(
        (state: any) => state.global.infoUser.profileAuthorization
    );

    const [modals, setModal] = useModalStates();
    const [filterActive, setLocalFilterActive] = useState(false);
    const [isAddProject, setIsAddProject] = useState(false);
    const [isProjectArchived, setIsProjectArchived] = useState(false);
    const [isAddNewAppClicked, setIsAddNewAppClicked] = useState(false);
    const [data, setData] = useState<any>({});
    const [typeDisplay, setTypeDisplay] = useState("notArchived");
    const [selectedRows, setSelectedRows] = useState<any>({});
    const [editingRow, setEditingRow] = useState<any>({});
    const [isDataUpdated, setIsDataUpdated] = useState<boolean | number>(false);
    const [numberAssociate, setNumberAssociate] = useState<
        Record<string, number>
    >({});
    const shopId = oneShop ? shopID : "0";

    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        isEdited
    );
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const isInitialMount = useRef(true);

    // Mémorisation de l'URL
    const url = useMemo(
        () =>
            `${API_PROJECT_URL}?userId=${userID}&shopId=${shopID}&franchiseId=${franchiseID}&typeDisplay=${typeDisplay}`,
        [userID, shopID, franchiseID, typeDisplay]
    );

    // Mémorisation des colonnes personnalisées
    const arrayOfCustomColumns: customColumnProps[] = useMemo(
        () => [
            {
                indexOFColumn: 999,
                columnName: t("Actions"),
                customJsx: (e: any) => (
                    <CustomIcons
                        e={e}
                        setIsAddProject={setIsAddProject}
                        isProjectArchived={isProjectArchived}
                        setSelectedRows={setSelectedRows}
                        setEditingRow={setEditingRow}
                        setIsHistoryModalOpened={(v: boolean) =>
                            setModal("history", v)
                        }
                        isHistoryModalOpened={modals.history}
                    />
                ),
                disableFilter: false,
            },
            {
                indexOFColumn: 1,
                columnName: t("Designation"),
                customJsx: (e: any) => (
                    <CustomColumnDisplay
                        text={e.selectedRow[t("Designation")]}
                    />
                ),
            },
            {
                indexOFColumn: 4,
                columnName: t("Status"),
                customJsx: (e: any) => (
                    <StatusBadge
                        row={e.selectedRow}
                        isArchived={isProjectArchived}
                        t={t}
                    />
                ),
            },
        ],
        [t, isProjectArchived, modals.history, setModal]
    );

    // Initialisation des autorisations
    useEffect(() => {
        setModify(
            getPathAuthorization(location.pathname, authorizationList).Modifie
        );
    }, [location.pathname, authorizationList]);

    // Gestion du filtre actif
    useEffect(() => {
        if (filterActive) setIsAddProject(false);
    }, [filterActive]);

    // Mise à jour des applications sélectionnées
    useEffect(() => {
        const array = Object.values(selectedRows)
            .filter((el: any) => el.projectId !== undefined)
            .map((el: any) => ({ [el.shopId]: el.projectId }));
        setSelectedApplicationSco(array);
    }, [selectedRows, shopID, oneShop, data, isProjectArchived]);

    // Mise à jour des compteurs et associations
    useEffect(() => {
        const { othersData } = data;
        setNumberActive(othersData?.activedProjects ?? 0);
        setNumberArchived(othersData?.archivedProjects ?? 0);

        const associates =
            othersData?.dataProjectFranchise?.data?.reduce(
                (acc: any, el: any) => {
                    acc[el.projectId] = Object.keys(el.associates).length;
                    return acc;
                },
                {}
            ) ?? {};
        setNumberAssociate(associates);
    }, [data]);

    // Gestion du changement de langue
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (["fr", "en"].includes(i18nextLng)) {
            setIsDataUpdated(true);
            setIsAddProject(false);
        }
        setIsEdited(false);
    }, [i18nextLng]);

    // Synchronisation des actions de navigation
    useEffect(() => {
        setActions(confirmNavigation);
        setIsActionsClicked(showPrompt as boolean);
        setValidationAction(cancelNavigation);
    }, [showPrompt, confirmNavigation, cancelNavigation]);

    const breadcrumbItems = useMemo(
        () => [
            {
                item: "Settings".toUpperCase(),
                link: `${process.env.REACT_APP_PORTAIL_URL}`,
            },
            {
                item: selectedAccount?.toUpperCase() || "",
                link: oneShop ? "/setting/customer-account" : "/compte",
            },
            { item: t("Application").toUpperCase(), link: "applications" },
            { item: t("POS").toUpperCase(), link: "pos" },
            {
                item: t(
                    typeDisplay !== "notArchived"
                        ? "Archived project"
                        : "Template Setup"
                ).toUpperCase(),
                link: "pos-project-setting",
            },
        ],
        [selectedAccount, oneShop, t, typeDisplay]
    );

    return (
        <>
            <PageTitle title={t("Applications")} />
            <div className="page-content__clz">
                <StyledBreadcrumb items={breadcrumbItems} />
                <MessageInfo
                    message={t(
                        "In this section, you have the ability to configure the POS, allowing you to customize them according to the specific needs of your store."
                    )}
                />

                <div
                    style={{
                        display: "grid",
                        minHeight: oneShop ? "650px" : "600px",
                        gridTemplateColumns: isAddProject ? "2fr 1fr" : "auto",
                        gridColumnGap: "4px",
                        border: 0,
                    }}
                >
                    <DynamicTable
                        url={url}
                        name="Item_table"
                        requestHeader={{
                            "Accept-Language": i18nextLng,
                            ...getAuthHeader,
                        }}
                        setData={setData}
                        canExpand
                        canSort
                        customSelect
                        canResize
                        canSelect
                        canMovedCheckboxLeftOnExpand
                        showGlobalFilter
                        showFilter
                        elevationTable={1}
                        minHeight="500px"
                        maxHeight="500px"
                        setSelectedRows={setSelectedRows}
                        filterActive={filterActive}
                        setLocalFilterActive={setLocalFilterActive}
                        setDataIsUpdated={setIsDataUpdated}
                        dataIsUpdated={isDataUpdated}
                        arrayOfCustomColumns={arrayOfCustomColumns}
                        customJsxSideFilterButton={
                            <CustomSideFilter
                                isAddProject={isAddProject}
                                setIsAddProject={setIsAddProject}
                                setLocalFilterActive={setLocalFilterActive}
                                setDataIsUpdated={setIsDataUpdated}
                                selectedRows={selectedRows}
                                setSelectedRows={setEditingRow}
                                isProjectArchived={isProjectArchived}
                                setIsProjectArchived={setIsProjectArchived}
                                shopId={shopId}
                                setIsConfirmationModalActived={(v: boolean) =>
                                    setModal("confirmActived", v)
                                }
                                setIsInInitializationModalOpened={(
                                    v: boolean
                                ) => setModal("inInitialization", v)}
                                setIsHistoryModalOpened={(v: boolean) =>
                                    setModal("history", v)
                                }
                                setIsConfirmationModalArchived={(v: boolean) =>
                                    setModal("confirmArchived", v)
                                }
                                setIsModalDissociateOpened={
                                    setIsModalDissociateOpened
                                }
                                isHistoryModalOpened={modals.history}
                                setTypeDisplay={setTypeDisplay}
                            />
                        }
                        actionColumn={(e: any) => (
                            <DropDownWrapper
                                element={e}
                                setSelectedRows={setSelectedRows}
                                selectedRows={selectedRows}
                                isProjectArchived={isProjectArchived}
                                setIsConfirmationModalArchived={(v: boolean) =>
                                    setModal("confirmArchived", v)
                                }
                                setIsConfirmationModalActived={(v: boolean) =>
                                    setModal("confirmActived", v)
                                }
                                setIsConfirmationModalCopied={(v: boolean) =>
                                    setModal("confirmCopied", v)
                                }
                                associates={numberAssociate}
                                setIsDataUpdated={setIsDataUpdated}
                            />
                        )}
                    />
                    {isAddProject && (
                        <AddProject
                            isAddProject={isAddProject}
                            setIsAddProject={setIsAddProject}
                            setDataIsUpdated={setIsDataUpdated}
                            isAddNewApplicationButtonClicked={
                                isAddNewAppClicked
                            }
                            data={data}
                            selectedRows={editingRow}
                            shopId={shopId}
                        />
                    )}
                </div>

                <ModalsContainer
                    selectedRows={selectedRows}
                    isInInitializationModalOpened={modals.inInitialization}
                    setIsInInitializationModalOpened={(v: boolean) =>
                        setModal("inInitialization", v)
                    }
                    isAddNewApplicationButtonClicked={isAddNewAppClicked}
                    setIsAddNewApplicationButtonClicked={setIsAddNewAppClicked}
                    setIsEdited={setIsEdited}
                    isHistoryModalOpened={modals.history}
                    setIsHistoryModalOpened={(v: boolean) =>
                        setModal("history", v)
                    }
                    isConfirmationModalArchived={modals.confirmArchived}
                    setIsConfirmationModalArchived={(v: boolean) =>
                        setModal("confirmArchived", v)
                    }
                    isConfirmationModalActived={modals.confirmActived}
                    setIsConfirmationModalActived={(v: boolean) =>
                        setModal("confirmActived", v)
                    }
                    isDeviceDissociateModalOpened={modals.deviceDissociate}
                    setIsDeviceDissociateModalOpened={(v: boolean) =>
                        setModal("deviceDissociate", v)
                    }
                    isModalAssociateStore={isModalAssociateStore}
                    isModalDissociateOpened={isModalDissociateOpened}
                    setIsModalDissociateOpened={setIsModalDissociateOpened}
                    isConfirmationModalCopied={modals.confirmCopied}
                    setIsConfirmationModalCopied={(v: boolean) =>
                        setModal("confirmCopied", v)
                    }
                    isActionsClicked={isActionsClicked}
                    setIsActionsClicked={setIsActionsClicked}
                    validationAction={validationAction}
                    actions={actions}
                    isModesModalPublishOpened={isModesModalPublishOpened}
                    setIsModesModalPublishOpened={setIsModesModalPublishOpened}
                    isDataUpdated={isDataUpdated}
                    setIsDataUpdated={setIsDataUpdated}
                />
            </div>
        </>
    );
}
